import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import { LoaderCircle, RefreshCw } from 'lucide-react';

import Loader from '../../Core/Components/Loader/Loader';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import { Label } from '@/Modules/Core/Components/shadcn/label';
import { Input } from '@/Modules/Core/Components/shadcn/input';
import { Textarea } from '@/Modules/Core/Components/shadcn/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Modules/Core/Components/shadcn/select';
import { useAuth } from '../../Core/Context/AuthContext';
import { useDebouncedValue } from '@/Modules/Core/Hooks/useDebouncedValue';
import { MultiSelect } from '@/Modules/Core/Components/shadcn/multi-select';
import TagInput from '@/Modules/Core/Components/Tags/TagInput';
import { cn } from '@/lib/utils';
import {
  getTagNames,
  mergeTagOptions,
  normalizeTagList,
} from '@/Modules/Core/Components/Tags/tagUtils';
import { searchRolloutTags } from '@/Modules/Core/Services/RolloutTagService';
import { navigateBackWithFallback } from '@/Utils/Routes/navigation';
import {
  getAllCauses,
  getProjects,
  getRolloutName,
  listVersions,
  saveRollout,
  syncProjects,
} from '../Services/ProjectService';
import { buildRolloutListLocation } from '../Components/ProjectCatalog/projectCatalogUtils';
import { buildCreateRolloutPayload } from './createRolloutPayload';

const PROJECT_PAGE_SIZE = 25;

const textPrimaryClass = 'text-[#111827] dark:text-foreground';
const textSecondaryClass = 'text-[#374151] dark:text-foreground/90';
const textMutedClass = 'text-[#6B7280] dark:text-muted-foreground';
const textDisabledClass = 'text-[#9CA3AF] dark:text-muted-foreground/70';
const surfaceBorderClass = 'border-[#E5E7EB] dark:border-border/60';
const cardSurfaceClass = `${surfaceBorderClass} bg-white dark:bg-background/[0.02]`;
const subduedSurfaceClass = `${surfaceBorderClass} bg-[#F9FAFB] dark:bg-muted/10`;
const focusAccentClass =
  'focus-visible:border-[#6366F1] focus-visible:ring-[#6366F1]/18 dark:focus-visible:border-ring dark:focus-visible:ring-ring/35';
const inlineActionClass =
  `h-auto px-0 py-0 text-[13px] font-medium ${textMutedClass} hover:bg-transparent hover:text-[#111827] dark:hover:text-foreground`;

const fieldLabelClass = `text-[13px] font-medium tracking-[0.01em] ${textSecondaryClass}`;
const fieldGroupClass = 'space-y-2.5';

const inputClassName =
  `h-11 rounded-xl border-[#E5E7EB] bg-white px-3.5 text-[#111827] placeholder:text-[#9CA3AF] shadow-none dark:border-border/70 dark:bg-background/90 dark:text-foreground dark:placeholder:text-muted-foreground ${focusAccentClass}`;
const textareaClassName =
  `min-h-[140px] rounded-xl border-[#E5E7EB] bg-white px-3.5 py-3 text-[#111827] placeholder:text-[#9CA3AF] shadow-none resize-none dark:border-border/70 dark:bg-background/90 dark:text-foreground dark:placeholder:text-muted-foreground ${focusAccentClass}`;
const selectTriggerClassName =
  `h-11 rounded-xl border-[#E5E7EB] bg-white px-3.5 text-[#111827] shadow-none dark:border-border/70 dark:bg-background/90 dark:text-foreground ${focusAccentClass}`;
const rolloutPrimaryGridClass =
  'grid gap-x-6 gap-y-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.78fr)]';
const rolloutEnvironmentConfigKey = 'MOCA_ENVIRONEMTNS';
const rolloutActiveAccentClass = 'text-[#C2410C] dark:text-[#ffb09c]';
const rolloutActiveAccentBorderClass =
  'border-[#F97316] bg-[#FFF7ED] text-[#C2410C] dark:border-[#ffb09c] dark:bg-background dark:text-[#ffb09c]';
const rolloutActiveAccentBadgeClass =
  'border border-[#FDBA74] bg-[#FFF7ED] text-[#C2410C] dark:border-transparent dark:bg-[#ffb09c]/10 dark:text-[#ffb09c]';
const EMPTY_RECREATE_ROLLOUT_STATE = {
  description: '',
  environment: '',
  tags: [],
};

const normalizeSelectionOption = (item) => {
  if (!item) {
    return null;
  }

  const value = item?.value ?? item?.id ?? item;
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return {
    value: String(value),
    label: String(item?.label ?? item?.name ?? value),
  };
};

const normalizeRolloutName = (value) => String(value ?? '').toUpperCase();

const parseRolloutEnvironmentOptions = (rawValue) => {
  if (!rawValue) {
    return [];
  }

  const parsedValue =
    typeof rawValue === 'string'
      ? (() => {
          try {
            return JSON.parse(rawValue);
          } catch (_error) {
            return [];
          }
        })()
      : rawValue;

  if (!Array.isArray(parsedValue)) {
    return [];
  }

  const dedupedOptions = new Map();

  parsedValue.forEach((item) => {
    const environmentName = String(
      item?.environment ?? item?.name ?? item?.label ?? ''
    ).trim();
    const isDefault = Boolean(
      item?.isDefault ?? item?.default ?? item?.is_default
    );

    if (!environmentName) {
      return;
    }

    const normalizedKey = environmentName.toLowerCase();

    if (!dedupedOptions.has(normalizedKey)) {
      dedupedOptions.set(normalizedKey, {
        value: environmentName,
        label: environmentName,
        majorVersion: String(item?.majorVersion ?? item?.major ?? ''),
        minorVersion: String(item?.minorVersion ?? item?.minor ?? ''),
        isDefault,
      });
      return;
    }

    if (isDefault) {
      dedupedOptions.set(normalizedKey, {
        ...dedupedOptions.get(normalizedKey),
        isDefault: true,
      });
    }
  });

  return Array.from(dedupedOptions.values());
};

const normalizeRecreateRolloutState = (state) => {
  const source = state?.recreateRollout;

  if (!source) {
    return EMPTY_RECREATE_ROLLOUT_STATE;
  }

  return {
    description: String(
      source?.description ?? source?.rolloutDescription ?? ''
    ).trim(),
    environment: String(
      source?.environment ?? source?.environmentName ?? ''
    ).trim(),
    tags: normalizeTagList(
      source?.tags ?? source?.tagNames ?? source?.labels
    ),
  };
};

const findEnvironmentOption = (options, value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();

  if (!normalizedValue) {
    return null;
  }

  return (
    options.find(
      (option) =>
        String(option?.value ?? '').trim().toLowerCase() === normalizedValue
    ) || null
  );
};

const findDefaultEnvironmentOption = (options = []) =>
  options.find((option) => Boolean(option?.isDefault)) || options[0] || null;

const buildRolloutRevision = (payload) => {
  const majorRevision = String(payload?.major_revision ?? '').trim();
  const minorRevision = String(payload?.minor_revision ?? '').trim();

  if (!majorRevision && !minorRevision) {
    return '';
  }

  if (!majorRevision) {
    return minorRevision;
  }

  if (!minorRevision) {
    return majorRevision;
  }

  return `${majorRevision}.${minorRevision}`;
};

const parseGeneratedRollout = (payload) => {
  const rolloutNameDetail = String(payload?.name ?? '').trim();
  const revisionVersion = buildRolloutRevision(payload);

  if (!rolloutNameDetail) {
    return {
      generatedName: '',
      generatedVersion: revisionVersion,
    };
  }

  if (
    revisionVersion &&
    rolloutNameDetail.endsWith(`--${revisionVersion}`)
  ) {
    return {
      generatedName: rolloutNameDetail.slice(0, -(revisionVersion.length + 2)),
      generatedVersion: revisionVersion,
    };
  }

  const lastDelimiterIndex = rolloutNameDetail.lastIndexOf('--');

  if (lastDelimiterIndex === -1) {
    return {
      generatedName: rolloutNameDetail,
      generatedVersion: revisionVersion,
    };
  }

  return {
    generatedName: rolloutNameDetail.slice(0, lastDelimiterIndex),
    generatedVersion:
      revisionVersion || rolloutNameDetail.slice(lastDelimiterIndex + 2),
  };
};

const SelectionSkeleton = ({ rows = 4 }) => (
  <div className="space-y-2" aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={`selection-skeleton-${index + 1}`}
        className="h-10 animate-pulse rounded-xl bg-muted"
      />
    ))}
  </div>
);

const MultiSelectSkeleton = () => (
  <div className="space-y-2" aria-hidden="true">
    <div className="flex min-h-10 w-full items-center justify-between gap-3 rounded-xl border border-input bg-background px-3 py-2">
      <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
        <div className="h-6 w-24 animate-pulse rounded-lg bg-muted" />
        <div className="h-6 w-20 animate-pulse rounded-lg bg-muted/80" />
        <div className="h-6 w-16 animate-pulse rounded-lg bg-muted/70" />
      </div>
      <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-muted" />
    </div>
    <div className="flex gap-2">
      <div className="h-3 w-28 animate-pulse rounded bg-muted/80" />
      <div className="h-3 w-20 animate-pulse rounded bg-muted/60" />
    </div>
  </div>
);

const InputFieldSkeleton = ({
  labelWidthClassName = 'w-24',
  helper,
  control,
}) => (
  <div className={fieldGroupClass} aria-hidden="true">
    <div className={cn('h-3 animate-pulse rounded bg-muted', labelWidthClassName)} />
    {control || <div className="h-11 animate-pulse rounded-xl bg-muted" />}
    {helper || null}
  </div>
);

const RolloutFormSkeleton = () => (
  <div
    className="space-y-3.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-200"
    aria-hidden="true"
  >
    <div className={rolloutPrimaryGridClass}>
      <InputFieldSkeleton labelWidthClassName="w-24" />
      <InputFieldSkeleton labelWidthClassName="w-16" />
    </div>

    <div className={rolloutPrimaryGridClass}>
      <InputFieldSkeleton
        labelWidthClassName="w-16"
        control={<MultiSelectSkeleton />}
      />
    </div>

    <div className={rolloutPrimaryGridClass}>
      <InputFieldSkeleton labelWidthClassName="w-20" />
      <InputFieldSkeleton
        labelWidthClassName="w-12"
        control={
          <div className="space-y-2">
            <div className="flex min-h-11 w-full items-center gap-2 rounded-xl border border-input bg-background px-3 py-2">
              <div className="h-6 w-16 animate-pulse rounded-full bg-muted" />
              <div className="h-6 w-20 animate-pulse rounded-full bg-muted/80" />
              <div className="h-4 flex-1 animate-pulse rounded bg-muted/50" />
              <div className="h-6 w-6 animate-pulse rounded-md bg-muted/70" />
            </div>
          </div>
        }
      />
    </div>

    <InputFieldSkeleton
      labelWidthClassName="w-20"
      control={<div className="min-h-[140px] animate-pulse rounded-xl bg-muted" />}
    />

    <div className="flex justify-end gap-3 pt-2" aria-hidden="true">
      <div className="h-10 w-24 animate-pulse rounded-xl bg-muted/60" />
      <div className="h-10 w-32 animate-pulse rounded-xl bg-muted" />
    </div>
  </div>
);

const CreateRollout = () => {
  const { projectId, versionId, action, major, name } = useParams();
  const location = useLocation();
  const { configData, refreshConfiguration } = useAuth();
  const navigate = useNavigate();

  const initialProject = useMemo(
    () =>
      normalizeSelectionOption(
        location.state?.project ?? location.state?.projectId
      ),
    [location.state]
  );
  const initialVersion = useMemo(
    () =>
      normalizeSelectionOption(
        location.state?.version ?? location.state?.versionId
      ),
    [location.state]
  );
  const effectiveAction = action || 'new';
  const recreateRolloutState = useMemo(
    () =>
      effectiveAction === 'recreate'
        ? normalizeRecreateRolloutState(location.state)
        : EMPTY_RECREATE_ROLLOUT_STATE,
    [effectiveAction, location.state]
  );

  const [projectOptions, setProjectOptions] = useState([]);
  const [projectOptionsSource, setProjectOptionsSource] = useState([]);
  const [projectOptionsSourceTotal, setProjectOptionsSourceTotal] = useState(0);
  const [projectPage, setProjectPage] = useState(1);
  const [projectTotal, setProjectTotal] = useState(0);
  const [projectHasMore, setProjectHasMore] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [isFetchingMoreProjects, setIsFetchingMoreProjects] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  const [project, setProject] = useState(initialProject);
  const [version, setVersion] = useState(initialVersion);
  const [versionOptions, setVersionOptions] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [syncingProjects, setSyncingProjects] = useState(false);

  const [isRolloutLoading, setIsRolloutLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBugsLoading, setIsBugsLoading] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [rolloutVersion, setRolloutVersion] = useState('');
  const [rolloutName, setRolloutName] = useState('');
  const [oldRolloutName, setOldRolloutName] = useState('');
  const [platform, setPlatform] = useState(configData?.DOWNLOAD_PLATFORM);
  const [environment, setEnvironment] = useState(
    recreateRolloutState.environment
  );
  const [selectedBugs, setSelectedBugs] = useState([]);
  const [selectedTags, setSelectedTags] = useState(recreateRolloutState.tags);
  const [rolloutDescription, setRolloutDescription] = useState(
    recreateRolloutState.description
  );
  const [tagOptions, setTagOptions] = useState(recreateRolloutState.tags);
  const [tagSearch, setTagSearch] = useState('');
  const [tagCatalogLoading, setTagCatalogLoading] = useState(false);
  const [noBugsWarning, setNoBugsWarning] = useState('');
  const [alertMessage, setAlertMessage] = useState({
    alert: false,
    message: null,
  });

  const loadingMoreRef = React.useRef(false);
  const latestProjectRequestRef = React.useRef(0);
  const lastLoadedProjectSearchRef = React.useRef(null);
  const projectSectionRef = React.useRef(null);
  const versionSectionRef = React.useRef(null);
  const formSectionRef = React.useRef(null);
  const debouncedProjectSearch = useDebouncedValue(projectSearch, 350);
  const configuredEnvironmentOptions = useMemo(
    () =>
      parseRolloutEnvironmentOptions(
        configData?.[rolloutEnvironmentConfigKey]
      ),
    [configData]
  );
  const environmentOptions = useMemo(() => {
    if (!recreateRolloutState.environment) {
      return configuredEnvironmentOptions;
    }

    if (
      findEnvironmentOption(
        configuredEnvironmentOptions,
        recreateRolloutState.environment
      )
    ) {
      return configuredEnvironmentOptions;
    }

    return [
      {
        value: recreateRolloutState.environment,
        label: recreateRolloutState.environment,
      },
      ...configuredEnvironmentOptions,
    ];
  }, [configuredEnvironmentOptions, recreateRolloutState.environment]);
  const isGuidedMode = !projectId || !versionId;
  const effectiveProjectId = isGuidedMode ? project?.value : projectId;
  const effectiveVersionId = isGuidedMode ? version?.value : versionId;
  const selectedProjectName = isGuidedMode
    ? project?.label ?? ''
    : String(
        location.state?.projectName ?? location.state?.project?.label ?? ''
      ).trim();
  const selectedVersionName = isGuidedMode
    ? version?.label ?? ''
    : String(
        location.state?.versionName ?? location.state?.version?.label ?? ''
      ).trim();
  const selectedProjectLabel = selectedProjectName || String(projectId ?? '');
  const selectedVersionLabel = selectedVersionName || String(versionId ?? '');

  const submitLabel = useMemo(() => {
    if (effectiveAction === 'recreate') return 'Recreate Rollout';
    if (effectiveAction === 'accum') return 'Create Cumulative Rollout';
    return 'Create Rollout';
  }, [effectiveAction]);

  const isLinuxPackage = platform === 'tar';
  const selectedBugCount = selectedBugs.length;
  const isSubmitDisabled =
    isSubmitting ||
    isRolloutLoading ||
    !rolloutName?.trim() ||
    !rolloutVersion?.trim() ||
    !rolloutDescription?.trim() ||
    selectedBugCount === 0;
  const wizardCurrentStep = !effectiveProjectId
    ? 1
    : !effectiveVersionId
      ? 2
      : 3;
  const canShowRolloutForm = Boolean(effectiveProjectId && effectiveVersionId);
  const shouldShowProjectSkeleton =
    isGuidedMode && projectLoading && !project && projectOptions.length === 0;
  const isProjectSearchPending =
    isGuidedMode &&
    projectLoading &&
    !project &&
    debouncedProjectSearch.trim() !== '';
  const shouldShowVersionSkeleton =
    isGuidedMode &&
    Boolean(project?.value) &&
    versionLoading &&
    !version &&
    versionOptions.length === 0;
  const shouldShowRolloutSkeleton =
    canShowRolloutForm &&
    isRolloutLoading &&
    !rolloutName?.trim() &&
    !rolloutVersion?.trim();
  const hasLoadedAllProjectRecords =
    projectOptionsSourceTotal > 0 &&
    projectOptionsSource.length >= projectOptionsSourceTotal;
  const rolloutScopeLabel = [
    selectedProjectLabel || effectiveProjectId,
    selectedVersionLabel || effectiveVersionId,
  ]
    .filter(Boolean)
    .join(' / ');
  const wizardSteps = [
    {
      id: 1,
      label: 'Select project',
      isActive: wizardCurrentStep === 1,
      isComplete: Boolean(effectiveProjectId),
    },
    {
      id: 2,
      label: 'Choose version',
      isActive: wizardCurrentStep === 2,
      isComplete: Boolean(effectiveProjectId && effectiveVersionId),
    },
    {
      id: 3,
      label: 'Rollout Details',
      isActive: wizardCurrentStep === 3,
      isComplete: false,
    },
  ];

  const showAlert = React.useCallback((message) => {
    setAlertMessage({
      alert: true,
      message,
    });
  }, []);

  useEffect(() => {
    refreshConfiguration();
  }, [refreshConfiguration]);

  useEffect(() => {
    if (!environmentOptions.length) {
      setEnvironment('');
      return;
    }

    setEnvironment((currentValue) => {
      const matchedCurrentOption = findEnvironmentOption(
        environmentOptions,
        currentValue
      );

      if (matchedCurrentOption) {
        return matchedCurrentOption.value;
      }

      const matchedRecreateOption = findEnvironmentOption(
        environmentOptions,
        recreateRolloutState.environment
      );

      if (matchedRecreateOption) {
        return matchedRecreateOption.value;
      }

      return findDefaultEnvironmentOption(environmentOptions)?.value || '';
    });
  }, [environmentOptions, recreateRolloutState.environment]);

  const mapToOption = React.useCallback((item) => {
    const option = normalizeSelectionOption({
      value: item?.id,
      label: item?.name,
    });

    return option;
  }, []);

  const loadProjects = React.useCallback(
    async ({ page = 1, search = projectSearch, append = false } = {}) => {
      const normalizedSearch = String(search ?? '').trim();
      const requestId = latestProjectRequestRef.current + 1;
      latestProjectRequestRef.current = requestId;

      if (append) {
        setIsFetchingMoreProjects(true);
      } else {
        setProjectLoading(true);
      }

      try {
        const response = await getProjects({
          pageNumber: page,
          pageSize: PROJECT_PAGE_SIZE,
          search: normalizedSearch,
        });

        if (requestId !== latestProjectRequestRef.current) {
          return;
        }

        if (!response?.isSuccess) {
          showAlert(response?.message || 'Error fetching projects.');
          return;
        }

        const incoming = (response.projects || [])
          .map(mapToOption)
          .filter(Boolean);

        setProjectTotal(response.totalCount ?? 0);
        setProjectPage(page);

        if (append) {
          setProjectOptions((prev) => {
            const byId = new Map(prev.map((item) => [item.value, item]));
            incoming.forEach((option) => byId.set(option.value, option));

            const deduped = Array.from(byId.values());
            const total = response.totalCount ?? 0;

            if (!normalizedSearch) {
              setProjectOptionsSource(deduped);
            }

            if (
              response.hasNextPage !== undefined &&
              response.hasNextPage !== null
            ) {
              setProjectHasMore(Boolean(response.hasNextPage));
            } else {
              setProjectHasMore(total ? deduped.length < total : false);
            }

            return deduped;
          });
        } else {
          setProjectOptions(incoming);
          if (!normalizedSearch) {
            setProjectOptionsSource(incoming);
            setProjectOptionsSourceTotal(response.totalCount ?? 0);
          }

          const total = response.totalCount ?? 0;
          if (
            response.hasNextPage !== undefined &&
            response.hasNextPage !== null
          ) {
            setProjectHasMore(Boolean(response.hasNextPage));
          } else {
            setProjectHasMore(total ? incoming.length < total : false);
          }
        }
      } catch (error) {
        if (requestId !== latestProjectRequestRef.current) {
          return;
        }

        showAlert(error?.message || 'Error fetching projects.');
      } finally {
        if (append) {
          setIsFetchingMoreProjects(false);
        } else if (requestId === latestProjectRequestRef.current) {
          setProjectLoading(false);
        }
      }
    },
    [mapToOption, projectSearch, showAlert]
  );

  const fetchVersionOptions = React.useCallback(
    async (projectKey) => {
      if (!projectKey) {
        setVersionOptions([]);
        return;
      }

      setVersionLoading(true);
      setVersionOptions([]);

      try {
        const data = await listVersions({
          projectKey,
          isServiceWorker: false,
        });

        if (!data?.isSuccess) {
          showAlert(data?.message || 'Unable to fetch versions.');
          return;
        }

        const mappedVersions = (data?.versions || [])
          .map((item) =>
            normalizeSelectionOption({
              value: item?.id,
              label: item?.name,
            })
          )
          .filter(Boolean);

        setVersionOptions((prev) => {
          const byId = new Map(prev.map((item) => [item.value, item]));
          mappedVersions.forEach((option) => byId.set(option.value, option));
          return Array.from(byId.values());
        });
      } catch (error) {
        showAlert(error?.message || 'Error fetching versions.');
      } finally {
        setVersionLoading(false);
      }
    },
    [showAlert]
  );

  const fetchRolloutName = React.useCallback(async () => {
    if (!effectiveProjectId || !effectiveVersionId) {
      return;
    }

    setIsRolloutLoading(true);

    try {
      const response = await getRolloutName(
        effectiveProjectId,
        effectiveVersionId,
        effectiveAction,
        major ?? 0,
        name
      );

      if (!response?.isSuccess) {
        showAlert(response?.message || 'Unable to generate rollout name.');
        return;
      }

      const { generatedName, generatedVersion } = parseGeneratedRollout(
        response?.payload
      );

      const sanitizedGeneratedName = normalizeRolloutName(
        generatedName.replace(/\s+/g, '')
      );

      setRolloutName(sanitizedGeneratedName);
      setOldRolloutName(sanitizedGeneratedName);
      setRolloutVersion(generatedVersion.replace(/\s+/g, ''));
    } catch (error) {
      showAlert(error?.message || 'Unable to generate rollout name.');
    } finally {
      setIsRolloutLoading(false);
    }
  }, [
    effectiveAction,
    effectiveProjectId,
    effectiveVersionId,
    major,
    name,
    showAlert,
  ]);

  const setBugsLabels = React.useCallback(
    (array) =>
      array?.map((item) => ({
        value: `${item?.key}`,
        label: `${item?.key}`,
      })),
    []
  );

  const fetchRolloutBugs = React.useCallback(async () => {
    if (!effectiveProjectId || !effectiveVersionId) {
      return;
    }

    setIsBugsLoading(true);

    try {
      const response = await getAllCauses(
        effectiveProjectId,
        effectiveVersionId,
        effectiveAction,
        name
      );

      if (!response?.isSuccess) {
        showAlert(response?.message || 'Unable to fetch bugs.');
        return;
      }

      if (response?.payload?.length === 0) {
        setNoBugsWarning(
          response?.status?.message || 'No bugs found for the selected version'
        );
        setBugs([]);
        return;
      }

      setNoBugsWarning('');
      setBugs(setBugsLabels(response?.payload));
    } catch (error) {
      showAlert(error?.message || 'Unable to fetch bugs.');
    } finally {
      setIsBugsLoading(false);
    }
  }, [
    effectiveAction,
    effectiveProjectId,
    effectiveVersionId,
    name,
    setBugsLabels,
    showAlert,
  ]);

  const platformChanged = (value) => {
    setPlatform(value === 'tar' ? 'tar' : 'zip');
  };

  const handleBugsChange = React.useCallback(() => {
    if (selectedBugs?.length > 0) {
      const bugId = selectedBugs?.length > 1 ? 'CUM' : selectedBugs[0];
      setRolloutName(
        normalizeRolloutName(oldRolloutName.replace('{BUGID}', bugId))
      );
    } else {
      setRolloutName(normalizeRolloutName(oldRolloutName));
    }
  }, [oldRolloutName, selectedBugs]);

  const handleSubmit = async () => {
    if (!effectiveProjectId || !effectiveVersionId) {
      return;
    }

    setIsSubmitting(true);
    const tagNames = getTagNames(selectedTags);

    const formData = buildCreateRolloutPayload({
      rolloutProduct: effectiveProjectId,
      rolloutReleaseVersion: effectiveVersionId,
      rolloutReleaseVersionName: '',
      rolloutVersion,
      rolloutName,
      environment,
      bugs: selectedBugs,
      tags: tagNames,
      platform: isLinuxPackage ? 'l' : 'w',
      rolloutDescription,
    });

    try {
      const response = await saveRollout(formData);

      if (!response?.isSuccess) {
        showAlert(response?.message || 'Error submitting form data.');
        return;
      }

      const payload = response?.payload;
      const resolvedRolloutPlatform = payload?.platform || formData.platform;
      const resolvedRolloutName =
        payload?.rolloutName || payload?.rollout_name || formData.rolloutName;
      const rolloutListLocation = buildRolloutListLocation({
        projectId: effectiveProjectId,
        versionId: effectiveVersionId,
        projectName: selectedProjectName,
        versionName: selectedVersionName,
      });

      navigate(
        rolloutListLocation?.pathname ||
          `/rolloutList/${effectiveProjectId}/${effectiveVersionId}`,
        {
          state: {
            ...(rolloutListLocation?.state || {}),
            downloadRolloutStatus: true,
            rolloutPlatform: resolvedRolloutPlatform,
            downloadRolloutName: resolvedRolloutName,
          },
        }
      );
    } catch (error) {
      showAlert(error?.message || 'Error submitting form data.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProjectChange = (selectedOption) => {
    setProject(selectedOption || null);
    setVersion(null);
    setVersionOptions([]);
    setAlertMessage({ alert: false, message: null });
  };

  const handleVersionChange = (selectedValue) => {
    setVersion(selectedValue || null);
    setAlertMessage({ alert: false, message: null });
  };

  const resetVersionSelection = () => {
    setVersion(null);
    setAlertMessage({ alert: false, message: null });
  };

  const scrollToSection = React.useCallback((ref) => {
    setTimeout(() => {
      ref?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 120);
  }, []);

  const handleProjectSearchChange = (value) => {
    if (value === projectSearch) {
      return;
    }

    setProjectSearch(value);
  };

  const handleLoadMoreProjects = React.useCallback(async () => {
    if (
      projectLoading ||
      isFetchingMoreProjects ||
      !projectHasMore ||
      loadingMoreRef.current
    ) {
      return;
    }

    loadingMoreRef.current = true;
    const requestedPage = projectPage + 1;

    await loadProjects({
      page: requestedPage,
      search: lastLoadedProjectSearchRef.current ?? '',
      append: true,
    });

    loadingMoreRef.current = false;
  }, [
    isFetchingMoreProjects,
    loadProjects,
    projectHasMore,
    projectLoading,
    projectPage,
  ]);

  const handleProjectListScroll = React.useCallback(
    (event) => {
      const target = event.currentTarget;
      const scrollProgress =
        (target.scrollTop + target.clientHeight) / target.scrollHeight;
      const isNearBottom = scrollProgress >= 0.88;

      if (isNearBottom) {
        handleLoadMoreProjects();
      }
    },
    [handleLoadMoreProjects]
  );

  const handleSyncProjects = async () => {
    try {
      setSyncingProjects(true);
      const response = await syncProjects();

      if (!response?.isSuccess) {
        showAlert(
          response?.message ||
            response?.status?.message ||
            'Project sync failed.'
        );
        return;
      }

      await loadProjects({ page: 1, search: projectSearch, append: false });
    } catch (error) {
      showAlert(error?.message || 'Project sync failed.');
    } finally {
      setSyncingProjects(false);
    }
  };

  const resetProjectSelection = () => {
    setProject(null);
    setVersion(null);
    setVersionOptions([]);
    setProjectSearch('');
    setAlertMessage({ alert: false, message: null });
    setProjectOptions(projectOptionsSource);
    setProjectTotal(projectOptionsSourceTotal || projectOptionsSource.length);
    lastLoadedProjectSearchRef.current = '';
  };

  const handleChangeProjectStep = () => {
    resetProjectSelection();
    scrollToSection(projectSectionRef);
  };

  const handleChangeVersionStep = () => {
    resetVersionSelection();
    scrollToSection(versionSectionRef);
  };

  const handleCancel = () => {
    navigateBackWithFallback(navigate, '/projects');
  };

  useEffect(() => {
    setProject(initialProject);
  }, [initialProject]);

  useEffect(() => {
    setVersion(initialVersion);
  }, [initialVersion]);

  useEffect(() => {
    if (!isGuidedMode) {
      return;
    }

    const normalizedSearch = debouncedProjectSearch.trim();
    const locallyFiltered = normalizedSearch
      ? projectOptionsSource.filter((item) =>
          item.label.toLowerCase().includes(normalizedSearch.toLowerCase())
        )
      : projectOptionsSource;

    if (projectOptionsSource.length > 0) {
      setProjectOptions(locallyFiltered);
    }

    if (!normalizedSearch && projectOptionsSource.length > 0) {
      setProjectTotal(projectOptionsSourceTotal || projectOptionsSource.length);
      lastLoadedProjectSearchRef.current = '';
      return;
    }

    if (normalizedSearch && hasLoadedAllProjectRecords) {
      setProjectTotal(projectOptionsSourceTotal || projectOptionsSource.length);
      return;
    }

    if (
      lastLoadedProjectSearchRef.current === normalizedSearch &&
      (!normalizedSearch || projectOptionsSource.length > 0)
    ) {
      return;
    }

    lastLoadedProjectSearchRef.current = normalizedSearch;
    loadProjects({ page: 1, search: normalizedSearch, append: false });
  }, [
    debouncedProjectSearch,
    isGuidedMode,
    loadProjects,
    hasLoadedAllProjectRecords,
    projectOptionsSource,
    projectOptionsSourceTotal,
  ]);

  useEffect(() => {
    if (!isGuidedMode || !project?.value) {
      return;
    }

    fetchVersionOptions(project.value);
  }, [fetchVersionOptions, isGuidedMode, project?.value]);

  useEffect(() => {
    setPlatform(configData?.DOWNLOAD_PLATFORM);
  }, [configData]);

  useEffect(() => {
    if (recreateRolloutState.tags.length === 0) {
      return;
    }

    setTagOptions((previousOptions) =>
      mergeTagOptions(previousOptions, recreateRolloutState.tags)
    );
  }, [recreateRolloutState.tags]);

  useEffect(() => {
    setSelectedBugs([]);
    setSelectedTags(recreateRolloutState.tags);
    setRolloutDescription(recreateRolloutState.description);
    setRolloutName('');
    setOldRolloutName('');
    setRolloutVersion('');
    setEnvironment(recreateRolloutState.environment);
    setBugs([]);
    setTagSearch('');
    setNoBugsWarning('');
    setAlertMessage((prev) => (prev.alert ? { ...prev, alert: false } : prev));

    if (!canShowRolloutForm) {
      setIsRolloutLoading(false);
      setIsBugsLoading(false);
    }
  }, [
    canShowRolloutForm,
    effectiveAction,
    effectiveProjectId,
    effectiveVersionId,
    recreateRolloutState.description,
    recreateRolloutState.environment,
    recreateRolloutState.tags,
  ]);

  useEffect(() => {
    if (!canShowRolloutForm) {
      return;
    }

    fetchRolloutName();
    fetchRolloutBugs();
  }, [canShowRolloutForm, fetchRolloutBugs, fetchRolloutName]);

  useEffect(() => {
    if (!canShowRolloutForm) {
      return undefined;
    }

    let isActive = true;

    const loadTags = async () => {
      setTagCatalogLoading(true);

      try {
        const response = await searchRolloutTags({
          search: tagSearch,
        });

        if (!isActive) {
          return;
        }

        if (response?.tags?.length > 0) {
          setTagOptions((previousOptions) =>
            mergeTagOptions(previousOptions, response.tags)
          );
        }
      } finally {
        if (isActive) {
          setTagCatalogLoading(false);
        }
      }
    };

    loadTags();

    return () => {
      isActive = false;
    };
  }, [canShowRolloutForm, tagSearch]);

  useEffect(() => {
    if (
      bugs &&
      bugs[0]?.value &&
      (effectiveAction === 'recreate' || effectiveAction === 'accum')
    ) {
      const values = bugs.map((item) => item?.value);
      setSelectedBugs(values);
    }
  }, [bugs, effectiveAction]);

  useEffect(() => {
    handleBugsChange();
  }, [handleBugsChange]);

  useEffect(() => {
    if (
      !isGuidedMode ||
      !project?.value ||
      version?.value ||
      !versionSectionRef.current
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      versionSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 180);

    return () => clearTimeout(timeout);
  }, [isGuidedMode, project?.value, version?.value]);

  useEffect(() => {
    if (!isGuidedMode || !version?.value || !formSectionRef.current) {
      return;
    }

    const timeout = setTimeout(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 180);

    return () => clearTimeout(timeout);
  }, [isGuidedMode, version?.value]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        setAlertMessage({ alert: false, message: null });
      }}
    >
      <React.Suspense fallback={<Loader />}>
        <div className="tw flex min-h-full flex-1 flex-col bg-[#F9FAFB] dark:bg-background">
          <div className="mr-auto ml-0 flex min-h-full w-full max-w-[980px] flex-1 flex-col px-4 py-8 sm:px-6">
            <div className="flex flex-1 flex-col gap-6">
              {alertMessage?.alert ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700 dark:border-destructive/40 dark:bg-destructive/10 dark:text-destructive-foreground">
                  {alertMessage.message}
                </div>
              ) : null}

              <div className="overflow-x-auto pb-1">
                <div className="flex min-w-[640px] items-center gap-4">
                  {wizardSteps.map((step, index) => (
                    <React.Fragment key={step.id}>
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={cn(
                            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-center text-[13px] leading-none font-semibold transition-colors',
                            step.isActive
                              ? rolloutActiveAccentBorderClass
                              : step.isComplete
                                ? 'border-[#D1D5DB] bg-white text-[#374151] dark:border-border/70 dark:bg-background dark:text-foreground/90'
                                : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF] dark:border-border dark:bg-transparent dark:text-muted-foreground/70'
                          )}
                        >
                          {step.id}
                        </span>
                        <span
                          className={cn(
                            'whitespace-nowrap text-[14px] transition-colors',
                            step.isActive
                              ? `font-semibold ${rolloutActiveAccentClass}`
                              : step.isComplete
                                ? 'font-medium text-[#374151] dark:text-foreground/90'
                                : 'font-medium text-[#9CA3AF] dark:text-muted-foreground/70'
                          )}
                        >
                          {step.label}
                        </span>
                      </div>

                      {index < wizardSteps.length - 1 ? (
                        <div className="h-px min-w-12 flex-1 bg-[#E5E7EB] dark:bg-border/70" />
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {!canShowRolloutForm ? (
                <section ref={projectSectionRef} className="space-y-3">
                  <div className={cn('rounded-2xl p-5', cardSurfaceClass)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <span
                            className={cn(
                              'inline-flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold',
                              rolloutActiveAccentBadgeClass
                            )}
                          >
                            1
                          </span>
                          <h2 className="text-[22px] font-semibold text-[#111827] dark:text-foreground">
                            Select a project
                          </h2>
                        </div>
                        <p className="pl-9 text-[13px] text-[#6B7280] dark:text-muted-foreground">
                          Search to find your project
                        </p>
                      </div>

                      {isGuidedMode ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={inlineActionClass}
                          onClick={handleSyncProjects}
                          disabled={projectLoading || syncingProjects}
                        >
                          <RefreshCw
                            className={cn(
                              'mr-1.5 h-3.5 w-3.5',
                              syncingProjects && 'animate-spin'
                            )}
                          />
                          {syncingProjects ? 'Syncing...' : 'Sync projects'}
                        </Button>
                      ) : null}
                    </div>

                    <div className="mt-5 space-y-2">
                      {isGuidedMode ? (
                        project ? (
                          <div className={cn('flex items-center justify-between gap-3 rounded-xl px-4 py-3', subduedSurfaceClass)}>
                            <div className="min-w-0">
                              <p className="truncate text-[15px] font-semibold text-[#111827] dark:text-foreground">
                                {project.label}
                              </p>
                              <p className="text-[12px] text-[#6B7280] dark:text-muted-foreground">
                                Project selected. Continue with version
                                selection.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className={inlineActionClass}
                              onClick={resetProjectSelection}
                            >
                              Change
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="relative">
                              <Input
                                value={projectSearch}
                                onChange={(event) =>
                                  handleProjectSearchChange(event.target.value)
                                }
                                placeholder="Search by project name..."
                                className={cn(inputClassName, 'pr-10')}
                              />
                              {isProjectSearchPending ? (
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[#6B7280] dark:text-muted-foreground">
                                  <LoaderCircle className="h-4 w-4 animate-spin" />
                                </span>
                              ) : null}
                            </div>

                            <p className="text-[12px] text-[#6B7280] dark:text-muted-foreground">
                              Showing {projectOptions.length} of {projectTotal}{' '}
                              projects
                            </p>

                            {shouldShowProjectSkeleton ? (
                              <SelectionSkeleton rows={5} />
                            ) : projectOptions.length > 0 ? (
                              <div
                                className="themed-slim-scrollbar max-h-[320px] space-y-2 overflow-y-auto pr-1"
                                onScroll={handleProjectListScroll}
                              >
                                {projectOptions.map((item) => (
                                  <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => handleProjectChange(item)}
                                    className={cn(
                                      'flex w-full items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-colors hover:border-[#CBD5E1] hover:bg-[#F9FAFB]',
                                      surfaceBorderClass,
                                      'dark:bg-background dark:hover:border-primary/40 dark:hover:bg-accent/30'
                                    )}
                                  >
                                    <div className="min-w-0">
                                      <p className="truncate text-[15px] font-semibold text-[#111827] dark:text-foreground">
                                        {item.label}
                                      </p>
                                      <p className="text-[12px] text-[#6B7280] dark:text-muted-foreground">
                                        Select this project to continue.
                                      </p>
                                    </div>
                                  </button>
                                ))}

                                {isFetchingMoreProjects ? (
                                  <div className="space-y-2 py-1">
                                    <div className="h-11 animate-pulse rounded-xl bg-muted" />
                                    <div className="h-11 animate-pulse rounded-xl bg-muted" />
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div className={cn('rounded-xl px-4 py-5 text-[13px] text-[#6B7280] dark:text-muted-foreground', subduedSurfaceClass)}>
                                {projectLoading
                                  ? 'Loading projects...'
                                  : 'No projects found'}
                              </div>
                            )}
                          </>
                        )
                      ) : (
                        <div className={cn('rounded-xl px-4 py-3', subduedSurfaceClass)}>
                          <p className="text-[15px] font-semibold text-[#111827] dark:text-foreground">
                            {selectedProjectLabel || effectiveProjectId}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              ) : null}

              {effectiveProjectId && !canShowRolloutForm ? (
                <section
                  ref={versionSectionRef}
                  className="space-y-3 animate-in fade-in-0 slide-in-from-bottom-1 duration-200"
                >
                  <div className={cn('rounded-2xl p-5', cardSurfaceClass)}>
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            'inline-flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold',
                            rolloutActiveAccentBadgeClass
                          )}
                        >
                          2
                        </span>
                        <h2 className="text-[22px] font-semibold text-[#111827] dark:text-foreground">
                          Choose version
                        </h2>
                      </div>
                      <p className="pl-9 text-[13px] text-[#6B7280] dark:text-muted-foreground">
                        Select the release version to continue.
                      </p>
                    </div>

                    <div className="mt-5 space-y-2">
                      {isGuidedMode ? (
                        version ? (
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-[#D1D5DB] bg-[#F9FAFB] px-4 py-3 dark:border-primary/40 dark:bg-primary/5">
                            <div className="min-w-0">
                              <p className="truncate text-[15px] font-semibold text-[#111827] dark:text-foreground">
                                {version.label}
                              </p>
                              <p className="text-[12px] text-[#374151] dark:text-primary">
                                Version selected. Continuing to rollout setup.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className={inlineActionClass}
                              onClick={resetVersionSelection}
                            >
                              Change
                            </Button>
                          </div>
                        ) : shouldShowVersionSkeleton ? (
                          <SelectionSkeleton rows={4} />
                        ) : (
                          <div className="space-y-3">
                            <p className="text-[12px] text-[#6B7280] dark:text-muted-foreground">
                              Showing {versionOptions.length} versions
                            </p>

                            {versionOptions.length > 0 ? (
                              <div className="themed-slim-scrollbar max-h-[280px] space-y-2 overflow-y-auto pr-1">
                                {versionOptions.map((item) => (
                                  <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => handleVersionChange(item)}
                                    className={cn(
                                      'flex w-full cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
                                      'border-[#E5E7EB] bg-white hover:border-[#CBD5E1] hover:bg-[#F9FAFB]',
                                      'dark:border-border/60 dark:bg-background dark:hover:border-primary/40 dark:hover:bg-accent/30'
                                    )}
                                  >
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-[15px] font-semibold text-[#111827] dark:text-foreground">
                                        {item.label}
                                      </p>
                                      <p className="mt-1 text-[12px] text-[#6B7280] dark:text-muted-foreground">
                                        Select this version to continue to
                                        rollout setup.
                                      </p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className={cn('rounded-xl px-4 py-5 text-[13px] text-[#6B7280] dark:text-muted-foreground', subduedSurfaceClass)}>
                                No versions were returned for this project.
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div className={cn('rounded-xl px-4 py-3', subduedSurfaceClass)}>
                          <p className="text-[15px] font-semibold text-[#111827] dark:text-foreground">
                            {selectedVersionLabel || effectiveVersionId}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              ) : null}

              {canShowRolloutForm ? (
                <section
                  ref={formSectionRef}
                  className="w-full self-start animate-in fade-in-0 slide-in-from-bottom-1 duration-200"
                >
                  <div
                    className={cn(
                      'relative space-y-6 rounded-[24px] p-6 sm:p-7',
                      cardSurfaceClass
                    )}
                    aria-busy={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="absolute inset-0 z-10 rounded-2xl bg-white/75 dark:bg-background/70">
                        <Loader label="Creating rollout..." />
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            'inline-flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold',
                            rolloutActiveAccentBadgeClass
                          )}
                        >
                          3
                        </span>
                        <h2 className="text-[22px] font-semibold text-[#111827] dark:text-foreground">
                          Rollout Details
                        </h2>
                      </div>
                      <p className="pl-9 text-[13px] text-[#6B7280] dark:text-muted-foreground">
                        {rolloutScopeLabel}
                      </p>
                    </div>

                    {isGuidedMode ? (
                      <div className="grid gap-3 lg:grid-cols-2">
                        <div className={cn('flex items-center justify-between gap-3 rounded-xl px-4 py-3', subduedSurfaceClass)}>
                          <div className="min-w-0">
                            <p className="text-[11px] font-medium tracking-wide text-[#6B7280] uppercase dark:text-muted-foreground">
                              Project
                            </p>
                            <p className="truncate text-[15px] font-semibold text-[#111827] dark:text-foreground">
                              {selectedProjectLabel || effectiveProjectId}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={inlineActionClass}
                            onClick={handleChangeProjectStep}
                          >
                            Change
                          </Button>
                        </div>

                        <div className={cn('flex items-center justify-between gap-3 rounded-xl px-4 py-3', subduedSurfaceClass)}>
                          <div className="min-w-0">
                            <p className="text-[11px] font-medium tracking-wide text-[#6B7280] uppercase dark:text-muted-foreground">
                              Version
                            </p>
                            <p className="truncate text-[15px] font-semibold text-[#111827] dark:text-foreground">
                              {selectedVersionLabel || effectiveVersionId}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={inlineActionClass}
                            onClick={handleChangeVersionStep}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    <div className={cn('space-y-3.5', isSubmitting && 'pointer-events-none opacity-60')}>
                      {shouldShowRolloutSkeleton ? (
                        <RolloutFormSkeleton />
                      ) : (
                        <>
                        <div className={rolloutPrimaryGridClass}>
                          <div className={fieldGroupClass}>
                            <Label
                              htmlFor="rolloutName"
                              className={fieldLabelClass}
                            >
                              Rollout Name
                            </Label>
                            <Input
                              id="rolloutName"
                              value={rolloutName}
                              onChange={(event) =>
                                setRolloutName(
                                  normalizeRolloutName(
                                    event?.target?.value ?? ''
                                  )
                                )
                              }
                              placeholder="Enter rollout name"
                              required
                              disabled={isRolloutLoading}
                              className={cn(inputClassName, 'uppercase')}
                            />
                          </div>

                          <div className={fieldGroupClass}>
                            <Label
                              htmlFor="rolloutVersion"
                              className={fieldLabelClass}
                            >
                              Version
                            </Label>
                            <Input
                              id="rolloutVersion"
                              value={rolloutVersion}
                              readOnly
                              aria-readonly="true"
                              placeholder="Version"
                              className={cn(
                                inputClassName,
                                'bg-[#F9FAFB] font-medium text-[#374151] dark:bg-background/90 dark:text-foreground/90'
                              )}
                            />
                          </div>
                        </div>

                        <div className={rolloutPrimaryGridClass}>
                          <div className={fieldGroupClass}>
                            <Label className={fieldLabelClass}>Bug IDs</Label>

                            {isBugsLoading ? (
                              <MultiSelectSkeleton />
                            ) : (
                              <MultiSelect
                                options={bugs}
                                value={selectedBugs}
                                onChange={setSelectedBugs}
                                placeholder="Select bugs"
                                searchPlaceholder="Search bugs"
                                maxHeight="280px"
                                allowCustomValues
                              />
                            )}

                            {noBugsWarning ? (
                              <p className="text-[12px] leading-6 text-amber-600 dark:text-amber-300">
                                {noBugsWarning}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className={rolloutPrimaryGridClass}>
                          <div className={fieldGroupClass}>
                            <Label
                              htmlFor="platform"
                              className={fieldLabelClass}
                            >
                              Platform
                            </Label>
                            <Select
                              value={platform}
                              onValueChange={platformChanged}
                            >
                              <SelectTrigger
                                id="platform"
                                className={selectTriggerClassName}
                              >
                                <SelectValue placeholder="Select format" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="zip">Zip</SelectItem>
                                <SelectItem value="tar">Tar</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className={fieldGroupClass}>
                            <Label className={fieldLabelClass}>Tags</Label>
                            <TagInput
                              value={selectedTags}
                              options={tagOptions}
                              onChange={setSelectedTags}
                              onSearchChange={setTagSearch}
                              loading={tagCatalogLoading}
                              placeholder="Search or create tags"
                              searchPlaceholder="Find tags or create a new one"
                              emptyMessage="No matching tags"
                              createMessage="Create tag"
                              controlClassName={cn(
                                'min-h-11 rounded-xl border-[#E5E7EB] bg-white px-3.5 text-[#111827] shadow-none dark:border-border/70 dark:bg-background/90 dark:text-foreground',
                                focusAccentClass
                              )}
                            />
                          </div>
                        </div>

                        <div className={fieldGroupClass}>
                          <Label
                            htmlFor="rolloutDescription"
                            className={fieldLabelClass}
                          >
                            Description
                          </Label>
                          <Textarea
                            id="rolloutDescription"
                            value={rolloutDescription}
                            onChange={(event) =>
                              setRolloutDescription(event.target.value)
                            }
                            placeholder="Add rollout notes"
                            rows={6}
                            className={textareaClassName}
                          />
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                          <Button variant="outline" onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant="default"
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                          >
                            {isSubmitting ? 'Saving...' : submitLabel}
                          </Button>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default CreateRollout;
